import { TextField } from "@mui/material";
import { Text } from "$components";
import { useProductFormContext } from "../contexts/ProductFormContext";
import CField, { CFieldInputProps } from "$forms/CField";
import CompanySelect from "$forms/CompanySelect";
import { CompanyType } from "$types";
import ProductSelect from "$forms/ProductSelect";
import CategorySelect from "$forms/CategorySelect";
import NumberInput from "../../../EDB/NumberInput";
import { useStyles } from "../styles/ProductForm.styles";
import useAuth from "$hooks/useAuth";
import useJStyles from "$hooks/useJStyles";
import { ProductService } from "$services";

const validateReferenceUniqueness = async (
  value: string,
  currentCompany: string,
  productId?: string
) => {
  if (!value?.length) {
    return null;
  }

  const checkProduct = await ProductService.getProductByReference({
    company: currentCompany,
    reference: value,
    // Only check uniqueness against catalogue products
    isReferenced: true,
  });

  const filteredProducts = checkProduct.filter(
    (existingProduct) => existingProduct._id !== productId
  );

  if (filteredProducts.length > 0) {
    return "Cette référence existe déjà";
  }

  return null;
};

export const ProductInformationSection = () => {
  const classes = useStyles();
  const jclasses = useJStyles();
  const { isAdmin, isClient } = useAuth();
  const {
    form,
    product,
    company,
    defaultValuesForm,
    canEditRefP,
    isAReceptionRefProduct,
    isReferenced,
    currentCompany,
  } = useProductFormContext();

  const {
    control,
    setValue,
    formState: { errors },
  } = form;
  const isEditionInactiveProduct = Boolean(product && !product.isActive);

  return (
    <>
      <div className={jclasses.formSectionHead}>
        <Text type="h3">Informations produit</Text>
      </div>

      {/* entreprise - select */}
      <div>
        <CField
          control={control}
          disabled={
            Boolean(product) ||
            company ||
            defaultValuesForm?.company ||
            isEditionInactiveProduct
          }
          name="company"
          required
          error={errors.company}
        >
          {(props: CFieldInputProps) => (
            <CompanySelect
              {...props}
              companyTypes={[CompanyType.CLIENT]}
              minCharsToTrigerSearch={0}
              rowsPerPage={99999}
              onChange={(data) => {
                if (data) {
                  props.onChange(data);
                  setValue("contact", null);
                }
              }}
            />
          )}
        </CField>
      </div>
      <div className={classes.newFormFieldsRow}>
        <CField
          label="Référence principale"
          disabled={
            (!isAdmin() && !isClient()) ||
            !canEditRefP ||
            isEditionInactiveProduct
          }
          control={control}
          name="reference"
          required={isReferenced}
          rules={{
            validate: {
              noLeadingOrTrailingSpace: (value) =>
                !value ||
                value.trim() === value ||
                "La référence ne doit pas contenir d'espace en début ou en fin.",
              isUnique: (value) =>
                validateReferenceUniqueness(
                  value,
                  currentCompany,
                  product?._id
                ),
            },
          }}
          error={errors.reference}
        />
      </div>
      {isReferenced && (
        <div className={classes.newFormFieldsRow}>
          <CField
            label="Référence secondaire"
            disabled={(!isAdmin() && !isClient()) || isEditionInactiveProduct}
            control={control}
            name="secondReference"
            error={errors.secondReference}
          />
        </div>
      )}
      <div className={classes.newFormFieldsRow}>
        <CField
          label="Désignation"
          disabled={(!isAdmin() && !isClient()) || isEditionInactiveProduct}
          control={control}
          name="designation"
          required
          error={errors.designation}
        />
      </div>
      <div className={classes.newFormFieldsRow}>
        <CField
          label="Fournisseur"
          control={control}
          name="supplier"
          disabled={(!isAdmin() && !isClient()) || isEditionInactiveProduct}
          error={errors.supplier}
        >
          {(props: CFieldInputProps) => (
            <CompanySelect
              {...props}
              companyTypes={[CompanyType.SUPPLIER]}
              ignoreSideBarCompanyFilter
              minCharsToTrigerSearch={0}
              rowsPerPage={99999}
              supplierId={company?._id}
            />
          )}
        </CField>
      </div>
      <div className={!isReferenced ? classes.newFormFieldsRow : null}>
        <div>
          <CField
            label="Unité de mesure"
            disabled={
              (!isAdmin() && !isClient()) ||
              isAReceptionRefProduct ||
              isEditionInactiveProduct
            }
            control={control}
            name="unit"
            required
            error={errors.unit}
          />
        </div>
        {!isReferenced && (
          <div className={classes.newFormFieldsCol}>
            <CField
              disabled={(!isAdmin() && !isClient()) || isEditionInactiveProduct}
              control={control}
              name="quantity"
              rules={{
                validate: {
                  value: (value) =>
                    value > 0 || "La quantité doit être supérieur à zéro",
                },
              }}
              error={errors.quantity}
            >
              {(props: CFieldInputProps) => (
                <NumberInput {...props} label="Quantité" isRequired />
              )}
            </CField>
          </div>
        )}
      </div>
      {isReferenced && (
        <>
          <div className={classes.newFormFieldsRow}>
            <CField
              control={control}
              disabled={(!isAdmin() && !isClient()) || isEditionInactiveProduct}
              name="description"
              label="Description"
              error={errors.description}
            >
              {(props) => (
                <TextField
                  {...props}
                  variant="outlined"
                  style={{ width: "100%" }}
                  multiline
                  minRows={3}
                />
              )}
            </CField>
          </div>
          <div className={classes.newFormFieldsRow}>
            <CField
              label="Produits de substitution"
              control={control}
              name="substitutionProducts"
              disabled={(!isAdmin() && !isClient()) || isEditionInactiveProduct}
              error={errors.substitutionProducts}
            >
              {(props: CFieldInputProps) => (
                <ProductSelect
                  {...props}
                  multiple
                  placeholder="Sélectionnez un/des produit(s)."
                  companyId={company?._id}
                  filterProductIds={[product?._id]}
                  searchByReference={false}
                  onChange={(data) => {
                    if (data) {
                      props.onChange(data);
                    }
                  }}
                />
              )}
            </CField>
          </div>
          <div className={classes.newFormFieldsRow}>
            <CField
              label="Catégories"
              control={control}
              name="categories"
              disabled={
                (!isAdmin() && !isClient()) ||
                isAReceptionRefProduct ||
                isEditionInactiveProduct
              }
              required
              error={errors.categories}
            >
              {(props: CFieldInputProps) => (
                <CategorySelect
                  {...props}
                  multiple
                  placeholder="Sélectionnez une/des catégorie(s)."
                  companyId={company?._id}
                  onChange={(data) => {
                    if (data) {
                      props.onChange(data);
                    }
                  }}
                  // Referencing category can be displayed because the field is disabled in this case
                  // and, thus, can't be chosen
                  showReferencingCategory={isAReceptionRefProduct}
                />
              )}
            </CField>
          </div>
        </>
      )}
    </>
  );
};
